import { useMemo } from 'react';

type UseAddableComboboxParams = {
  inputSearch: string;
  value: string[] | undefined;
  items: string[];
};

type UseAddableComboboxReturn = {
  needsAddOption: boolean;
  isAddOptionSelected: boolean;
  itemsWithAddOption: string[];
  isAddOption: (item: string) => boolean;
};

export const useAddableCombobox = ({
  inputSearch,
  value: selectedValues,
  items: suggestions,
}: UseAddableComboboxParams): UseAddableComboboxReturn => {
  const needsAddOption = useMemo(
    () => inputSearch.length > 0 && !suggestions.some((option) => option === inputSearch),
    [inputSearch, suggestions],
  );

  const isAddOptionSelected = useMemo(
    () => (selectedValues ?? []).some((item) => item === inputSearch),
    [inputSearch, selectedValues],
  );

  const itemsWithAddOption = useMemo(() => {
    const seen = new Set<string>();
    const merged = needsAddOption ? [inputSearch, ...suggestions] : [...suggestions];

    return merged.filter((item) => {
      if (item.length === 0 || seen.has(item)) {
        return false;
      }

      seen.add(item);
      return true;
    });
  }, [inputSearch, needsAddOption, suggestions]);

  return {
    needsAddOption,
    isAddOptionSelected,
    itemsWithAddOption,
    isAddOption: (item: string) => needsAddOption && item === inputSearch,
  };
};
